import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store, Actions, ofActionSuccessful, Select } from '@ngxs/store';
import { Observable, Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddDriverTrackingCommand } from '@core/services/api.service';
import {
    AddDriverTrackingAction,
    AddDriverTrackingSuccessAction,
    AddDriverTrackingFailAction
} from '@ngxs-store/driver-tracking-store/driver-tracking.actions';
import { DriverTrackingState } from '@ngxs-store/driver-tracking-store/driver-tracking.state';
import { UiState } from '@ngxs-store/ui-store/ui.state';

interface CountryCode {
    code: string;
    name: string;
    flag: string;
    dropLeadingZero: boolean;
}

@Component({
    selector: 'app-add-driver-dialog',
    templateUrl: './add-driver-dialog.component.html',
    styleUrls: ['./add-driver-dialog.component.scss']
})
export class AddDriverDialogComponent implements OnInit, OnDestroy {
    @Select(UiState.getTheme) theme$: Observable<string>;

    driverForm: FormGroup;
    validationError: string = '';
    isLoading$: Observable<boolean>;
    currentTheme: string = 'light-theme';
    private onDestroy = new Subject<void>();
    private subscriptions: Subscription[] = [];

    countryCodes: CountryCode[] = [
        { code: '+27',  name: 'South Africa',      flag: 'ZA', dropLeadingZero: true },
        { code: '+43',  name: 'Austria',           flag: 'AT', dropLeadingZero: true },
        { code: '+32',  name: 'Belgium',           flag: 'BE', dropLeadingZero: true },
        { code: '+359', name: 'Bulgaria',          flag: 'BG', dropLeadingZero: true },
        { code: '+385', name: 'Croatia',           flag: 'HR', dropLeadingZero: true },
        { code: '+357', name: 'Cyprus',            flag: 'CY', dropLeadingZero: false },
        { code: '+420', name: 'Czech Republic',    flag: 'CZ', dropLeadingZero: true },
        { code: '+45',  name: 'Denmark',           flag: 'DK', dropLeadingZero: false },
        { code: '+372', name: 'Estonia',           flag: 'EE', dropLeadingZero: false },
        { code: '+358', name: 'Finland',           flag: 'FI', dropLeadingZero: true },
        { code: '+33',  name: 'France',            flag: 'FR', dropLeadingZero: true },
        { code: '+49',  name: 'Germany',           flag: 'DE', dropLeadingZero: true },
        { code: '+30',  name: 'Greece',            flag: 'GR', dropLeadingZero: true },
        { code: '+36',  name: 'Hungary',           flag: 'HU', dropLeadingZero: true },
        { code: '+354', name: 'Iceland',           flag: 'IS', dropLeadingZero: false },
        { code: '+353', name: 'Ireland',           flag: 'IE', dropLeadingZero: true },
        { code: '+39',  name: 'Italy',             flag: 'IT', dropLeadingZero: true },
        { code: '+371', name: 'Latvia',            flag: 'LV', dropLeadingZero: false },
        { code: '+423', name: 'Liechtenstein',     flag: 'LI', dropLeadingZero: false },
        { code: '+370', name: 'Lithuania',         flag: 'LT', dropLeadingZero: false },
        { code: '+352', name: 'Luxembourg',        flag: 'LU', dropLeadingZero: false },
        { code: '+356', name: 'Malta',             flag: 'MT', dropLeadingZero: false },
        { code: '+31',  name: 'Netherlands',       flag: 'NL', dropLeadingZero: true },
        { code: '+47',  name: 'Norway',            flag: 'NO', dropLeadingZero: false },
        { code: '+48',  name: 'Poland',            flag: 'PL', dropLeadingZero: true },
        { code: '+351', name: 'Portugal',          flag: 'PT', dropLeadingZero: true },
        { code: '+40',  name: 'Romania',           flag: 'RO', dropLeadingZero: true },
        { code: '+421', name: 'Slovakia',          flag: 'SK', dropLeadingZero: true },
        { code: '+386', name: 'Slovenia',          flag: 'SI', dropLeadingZero: true },
        { code: '+34',  name: 'Spain',             flag: 'ES', dropLeadingZero: false },
        { code: '+46',  name: 'Sweden',            flag: 'SE', dropLeadingZero: true },
        { code: '+41',  name: 'Switzerland',       flag: 'CH', dropLeadingZero: true },
        { code: '+44',  name: 'United Kingdom',    flag: 'GB', dropLeadingZero: true }
    ];

    constructor(
        private fb: FormBuilder,
        private store: Store,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
        public dialogRef: MatDialogRef<AddDriverDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            amcoId: number;
            isEditMode?: boolean;
            existingDriverName?: string;
            existingCellphoneNumber?: string;
        }
    ) {
        this.isLoading$ = this.store.select(DriverTrackingState.getIsLoading);
        this.initializeForm();
    }

    ngOnInit(): void {
        this.setupActionSubscriptions();
        this.setupThemeSubscription();
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.complete();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private initializeForm(): void {
        // Parse existing phone number if in edit mode
        let initialName = '';
        let initialCountryCode = '+27';
        let initialPhoneNumber = '';

        if (this.data.isEditMode && this.data.existingDriverName && this.data.existingCellphoneNumber) {
            initialName = this.data.existingDriverName;

            const phoneNumber = this.data.existingCellphoneNumber.trim();

            const matchingCountry = this.countryCodes.find(country =>
                phoneNumber.startsWith(country.code)
            );

            if (matchingCountry) {
                initialCountryCode = matchingCountry.code;
                initialPhoneNumber = phoneNumber.substring(matchingCountry.code.length);

                if (matchingCountry.dropLeadingZero && initialPhoneNumber.startsWith('0')) {
                    initialPhoneNumber = initialPhoneNumber.substring(1);
                }
            } else {
                const countryCodeMatch = phoneNumber.match(/^(\+\d{1,4})/);
                if (countryCodeMatch) {
                    initialCountryCode = countryCodeMatch[1];
                    initialPhoneNumber = phoneNumber.substring(countryCodeMatch[1].length);
                } else {
                    initialPhoneNumber = phoneNumber;
                }
            }
        }

        this.driverForm = this.fb.group({
            name: [initialName, [
                Validators.required,
                Validators.minLength(2),
                Validators.maxLength(100)
            ]],
            countryCode: [initialCountryCode, [
                Validators.required
            ]],
            phoneNumber: [initialPhoneNumber, [
                Validators.required,
                Validators.pattern(/^\d{6,15}$/)
            ]]
        });
    }

    private setupActionSubscriptions(): void {
        // Listen for successful driver tracking addition
        const successSub = this.actions$
            .pipe(ofActionSuccessful(AddDriverTrackingSuccessAction))
            .subscribe((action: AddDriverTrackingSuccessAction) => {
                if (action.payload.response.success) {
                    this.dialogRef.close({
                        success: true,
                        response: action.payload.response
                    });
                } else {
                    this.validationError = action.payload.response.message || 'Failed to add driver tracking';
                    this.cdr.detectChanges();
                }
            });

        // Listen for failed driver tracking addition
        const failSub = this.actions$
            .pipe(ofActionSuccessful(AddDriverTrackingFailAction))
            .subscribe((action: AddDriverTrackingFailAction) => {
                this.validationError = 'An error occurred while adding driver tracking. Please try again.';
                this.cdr.detectChanges();
            });

        this.subscriptions.push(successSub, failSub);
    }

    private setupThemeSubscription(): void {
        this.theme$.pipe(takeUntil(this.onDestroy)).subscribe(theme => {
            this.currentTheme = theme;
        });
    }

    onCancelClick(): void {
        this.dialogRef.close({ success: false });
    }

    onAddClick(): void {
        if (this.driverForm.invalid) {
            this.markFormGroupTouched();
            return;
        }

        this.validationError = '';

        const formValue = this.driverForm.value;

        // Get the selected country to check if we should drop leading zero
        const selectedCountry = this.countryCodes.find(c => c.code === formValue.countryCode);

        // Remove leading zero only if the country requires it
        let phoneNumber = formValue.phoneNumber;
        if (selectedCountry?.dropLeadingZero && phoneNumber.startsWith('0')) {
            phoneNumber = phoneNumber.substring(1);
        }

        const fullPhoneNumber = `${formValue.countryCode}${phoneNumber}`.trim();

        const command = new AddDriverTrackingCommand({
            amcoId: this.data.amcoId,
            name: formValue.name.trim(),
            cellphoneNumber: fullPhoneNumber
        });

        this.store.dispatch(new AddDriverTrackingAction(command));
    }

    private markFormGroupTouched(): void {
        Object.keys(this.driverForm.controls).forEach(key => {
            const control = this.driverForm.get(key);
            control?.markAsTouched();
        });
    }

    get isLoading(): boolean {
        return this.store.selectSnapshot(DriverTrackingState.getIsLoading);
    }

    getSelectedCountryDisplay(value: string): string {
        const country = this.countryCodes.find(c => c.code === value);
        return country ? `${country.flag} ${country.name} ${country.code}` : value;
    }

    get selectedCountry(): CountryCode | undefined {
        const value = this.driverForm.get('countryCode')?.value;
        return this.countryCodes.find(c => c.code === value);
    }

    get isEditMode(): boolean {
        return this.data.isEditMode || false;
    }

    get dialogTitle(): string {
        return this.isEditMode ? 'Edit Driver' : 'Add Driver';
    }

    get buttonText(): string {
        return this.isEditMode ? 'Update Driver' : 'Add Driver';
    }

    get buttonIcon(): string {
        return this.isEditMode ? 'edit' : 'person_add';
    }
}
